/**
 * RegisterPage.tsx — Registro por enlace (`/register?code=X`).
 *
 * Wizard de 2 pasos + estado final (spec registro-por-enlace-design §4.7):
 *   1. Telegram: el SERVIDOR emite el token (POST /api/register/telegram-token) y la web abre el deep-link del bot; el
 *      bot pide el contacto y valida que sea el del propio usuario. El teléfono
 *      sale de ahí — nunca se escribe en esta página.
 *   2. Acceso: Google o correo + contraseña (nombre y apellido prellenados desde
 *      Telegram, editables).
 *   Final: "Solicitud enviada". No se abre ninguna sesión: un admin debe aprobar.
 */
import { useFieldErrors } from '../hooks/useFieldErrors';
import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import {
  Text, Input, Button, Spinner, Field, MessageBar, MessageBarBody,
  makeStyles, tokens,
} from '@fluentui/react-components';
import { apiClient } from '../api/client';
import { useT } from '../i18n';
import { useLocaleStore } from '../store/locale.store';
import { usePageTitle } from '../hooks/usePageTitle';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { NewPasswordFields } from '../components/shared/PasswordField';
import { getTenantConfig } from '../api/tenant.api';
import { MIN_PASSWORD_LENGTH, isStrongPassword, isValidNewPassword } from '../utils/validators';

const POLL_INTERVAL_MS = 2000;
/** Tope del polling: coincide con el TTL de 10 min del vínculo en el backend. */
const POLL_MAX_TICKS = (10 * 60 * 1000) / POLL_INTERVAL_MS;

const useStyles = makeStyles({
  root: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    minHeight: '100vh', backgroundColor: tokens.colorNeutralBackground2,
  },
  card: {
    display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1, padding: '40px',
    borderRadius: tokens.borderRadiusLarge, boxShadow: tokens.shadow16,
    maxWidth: '420px', width: '100%',
  },
  names: { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: tokens.spacingHorizontalM },
  center: { display: 'flex', justifyContent: 'center' },
  or: { textAlign: 'center', color: tokens.colorNeutralForeground3 },
});

interface TenantInfo { tenantId: string; displayName: string; logoUrl?: string }
interface VerifyState { linked: boolean; phoneVerified: boolean; phoneMasked?: string; firstName?: string; lastName?: string }

const storageKey = (code: string) => `agrovid.register.token.${code}`;

/** sessionStorage puede estar bloqueado (modo privado): nunca debe romper la página. */
function readStoredToken(code: string | null): string | null {
  if (!code) return null;
  try { return sessionStorage.getItem(storageKey(code)); } catch { return null; }
}
function storeToken(code: string | null, token: string): void {
  if (!code) return;
  try { sessionStorage.setItem(storageKey(code), token); } catch { /* sin persistencia */ }
}

const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim()) && e.trim().length <= 254;

export default function RegisterPage() {
  const s = useStyles();
  const t = useT();
  const locale = useLocaleStore(st => st.locale);
  usePageTitle(t.pageTitle.register);
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  const [tenantInfo, setTenantInfo] = useState<TenantInfo | null>(null);
  const [resolveError, setResolveError] = useState('');
  const [loading, setLoading] = useState(!!code);

  // El token lo emite el servidor: viaja en el deep-link de Telegram y es la única
  // prueba de identidad del registro. Se recuerda en sessionStorage por si recarga
  // la página; si no hay uno guardado se pide uno nuevo al backend.
  const [telegramToken, setTelegramToken] = useState<string | null>(() => readStoredToken(code));
  const [tokenError, setTokenError] = useState(false);

  const requestToken = useCallback(async () => {
    setTokenError(false);
    try {
      const res = await apiClient.post('/api/register/telegram-token');
      const token = res.data?.token as string | undefined;
      if (!token) throw new Error('no token');
      storeToken(code, token);
      setTelegramToken(token);
    } catch {
      setTokenError(true);
    }
  }, [code]);
  const [verify, setVerify] = useState<VerifyState>({ linked: false, phoneVerified: false });
  const [timedOut, setTimedOut] = useState(false);

  const [name, setName] = useState('');
  const [lastName, setLastName] = useState('');
  const [usePassword, setUsePassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [minLen, setMinLen] = useState<number | undefined>(undefined);
  // Registro web sin Telegram: se salta el paso 1 y el teléfono es opcional.
  const [webMode, setWebMode] = useState(false);
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (!code) return;
    apiClient
      .get(`/api/register/resolve?code=${encodeURIComponent(code)}`)
      .then(res => {
        setTenantInfo(res.data);
        // Largo mínimo de la empresa del enlace; si falla se queda el default (8).
        if (res.data?.tenantId) getTenantConfig(res.data.tenantId).then(c => setMinLen(c.passwordMinLength)).catch(() => undefined);
      })
      .catch(() => setResolveError(t.register.invalidCode))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  useEffect(() => {
    if (code && !telegramToken) void requestToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  // Polling hasta que el bot confirme el teléfono verificado (con tope de tiempo).
  useEffect(() => {
    if (!code || !telegramToken || verify.phoneVerified || timedOut || !tenantInfo) return;
    let ticks = 0;
    const interval = setInterval(async () => {
      ticks += 1;
      if (ticks > POLL_MAX_TICKS) {
        clearInterval(interval);
        setTimedOut(true);
        return;
      }
      try {
        const res = await apiClient.get(`/api/register/verify-telegram?token=${encodeURIComponent(telegramToken ?? '')}`);
        const data = res.data as VerifyState;
        setVerify(data);
        if (data.phoneVerified) {
          setName(prev => prev || data.firstName || '');
          setLastName(prev => prev || data.lastName || '');
          clearInterval(interval);
        }
      } catch {
        // se sigue intentando en silencio
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [code, tenantInfo, telegramToken, verify.phoneVerified, timedOut]);

  const regenerateLink = () => {
    setTelegramToken(null);
    void requestToken();
    setVerify({ linked: false, phoneVerified: false });
    setTimedOut(false);
  };

  const submit = useCallback(async (body: Record<string, string>) => {
    setSubmitting(true);
    setSubmitError('');
    try {
      // En modo web no se envía el vínculo de Telegram; el teléfono es opcional.
      const webFields = webMode
        ? { ...(phone.trim() ? { phoneNumber: phone.trim() } : {}) }
        : { telegramLinkToken: telegramToken };
      await apiClient.post(`/api/register/submit?lang=${locale}`, {
        code, ...webFields, name: name.trim(), lastName: lastName.trim(), ...body,
      });
      try { sessionStorage.removeItem(storageKey(code ?? '')); } catch { /* ignore */ }
      setSent(true);
    } catch (e: any) {
      const status = e?.response?.status;
      setSubmitError(
        status === 429 ? t.register.tooManyAttempts
        : status === 401 ? t.register.googleFailed
        // 409: el correo o el teléfono ya existen en la empresa; el servidor lo dice (ya traducido).
        : status === 409 && typeof e?.response?.data?.error === 'string' ? e.response.data.error
        : t.register.genericError,
      );
    } finally {
      setSubmitting(false);
    }
  }, [code, telegramToken, name, lastName, locale, t, webMode, phone]);

  const fe = useFieldErrors();

  // Teléfono opcional: vacío es válido; si viene, formato internacional (+ y 8 a 15 dígitos).
  const phoneOk = (v: string) => !v.trim() || /^\+[1-9]\d{7,14}$/.test(v.replace(/[\s\-.()]/g, ''));

  const handleGoogle = (r: { credential?: string }) => {
    if (!r.credential) { setSubmitError(t.register.googleFailed); return; }
    if (!fe.validate({
      name: !name.trim() && t.register.nameRequired,
      phone: webMode && !phoneOk(phone) && t.register.phoneInvalid,
    })) return;
    void submit({ method: 'google', idToken: r.credential });
  };

  const handlePasswordSubmit = () => {
    const ok = fe.validate({
      name: !name.trim() && t.register.nameRequired,
      email: !isValidEmail(email) && t.register.emailInvalid,
      password: (password.length < MIN_PASSWORD_LENGTH || !isStrongPassword(password)) && t.register.passwordTooShort,
      confirm: password.length >= MIN_PASSWORD_LENGTH && isStrongPassword(password) && password !== confirmPassword && t.register.passwordMismatch,
      phone: webMode && !phoneOk(phone) && t.register.phoneInvalid,
    });
    if (!ok) return;
    void submit({ method: 'password', email: email.trim(), password });
  };

  const botUsername = import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string;
  const telegramLink = telegramToken ? `https://t.me/${botUsername}?start=regverify_${telegramToken}` : undefined;

  const shell = (children: React.ReactNode) => (
    <div className={s.root}><div className={s.card}>{children}</div></div>
  );

  if (!code) {
    return shell(<>
      <Text size={500} weight="semibold">{t.register.title}</Text>
      <MessageBar intent="warning"><MessageBarBody>{t.register.needInviteLink}</MessageBarBody></MessageBar>
    </>);
  }

  if (loading) {
    return <div className={s.root}><Spinner label={t.register.loading} /></div>;
  }

  if (resolveError || !tenantInfo) {
    return shell(
      <MessageBar intent="error"><MessageBarBody>{resolveError || t.register.invalidCode}</MessageBarBody></MessageBar>,
    );
  }

  if (sent) {
    return shell(<>
      <Text size={500} weight="semibold">{webMode ? t.register.webPendingTitle : t.register.pendingTitle}</Text>
      <MessageBar intent="success">
        <MessageBarBody>{webMode ? t.register.webPendingMessage : t.register.pendingMessage}</MessageBarBody>
      </MessageBar>
    </>);
  }

  // ── Paso 1: Telegram (o registro web sin Telegram) ────────────────────────
  if (!webMode && !verify.phoneVerified) {
    return shell(<>
      <Text size={500} weight="semibold">{t.register.titleFor(tenantInfo.displayName)}</Text>
      <Text weight="semibold">{t.register.stepTelegram}</Text>
      <Text>{t.register.telegramIntro}</Text>
      <Button appearance="subtle" onClick={() => setWebMode(true)}>{t.register.webModeLink}</Button>
      {telegramLink ? (
        <Button as="a" href={telegramLink} target="_blank" rel="noreferrer" appearance="primary">
          {t.register.openTelegram}
        </Button>
      ) : tokenError ? (
        <Button appearance="primary" onClick={() => void requestToken()}>{t.register.retry}</Button>
      ) : (
        <Spinner size="tiny" label={t.register.loading} />
      )}
      {timedOut ? (
        <>
          <MessageBar intent="warning"><MessageBarBody>{t.register.telegramTimeout}</MessageBarBody></MessageBar>
          <Button onClick={regenerateLink}>{t.register.retry}</Button>
        </>
      ) : (
        <Text size={200} role="status" style={{ color: tokens.colorNeutralForeground3 }}>
          {verify.linked ? t.register.telegramLinkedWaitingPhone : t.register.waitingTelegram}
        </Text>
      )}
    </>);
  }

  // ── Paso 2: método de acceso ──────────────────────────────────────────────
  return shell(<>
    <Text size={500} weight="semibold">{t.register.titleFor(tenantInfo.displayName)}</Text>
    {webMode ? (
      <Text weight="semibold">{t.register.webTitle}</Text>
    ) : (
      <>
        <Text weight="semibold">{t.register.stepAccess}</Text>
        <MessageBar intent="success">
          <MessageBarBody>{t.register.phoneVerified(verify.phoneMasked ?? '')}</MessageBarBody>
        </MessageBar>
      </>
    )}

    <div className={s.names}>
      <Field label={t.register.nameLabel} {...fe.props('name')}>
        <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} maxLength={80} />
      </Field>
      <Field label={t.register.lastNameLabel}>
        <Input value={lastName} onChange={(_, d) => setLastName(d.value)} maxLength={80} />
      </Field>
    </div>

    {webMode && (
      <Field label={t.register.phoneLabel} {...fe.props('phone')}>
        <Input type="tel" value={phone} onChange={(_, d) => { setPhone(d.value); fe.clear('phone'); }} />
      </Field>
    )}

    {submitError && <MessageBar intent="error"><MessageBarBody>{submitError}</MessageBarBody></MessageBar>}

    <div className={s.center}>
      <GoogleSignInButton onCredential={handleGoogle} locale={locale} />
    </div>
    <Text className={s.or}>{t.register.methodOr}</Text>

    {!usePassword ? (
      <Button onClick={() => setUsePassword(true)} disabled={submitting}>{t.register.methodPassword}</Button>
    ) : (
      <>
        <Field label={t.register.emailLabel} {...fe.props('email')}>
          <Input type="email" value={email} onChange={(_, d) => { setEmail(d.value); fe.clear('email'); }} />
        </Field>
        <NewPasswordFields
          minLength={minLen}
          password={password}
          confirm={confirmPassword}
          onPasswordChange={(v) => { setPassword(v); fe.clear('password'); fe.clear('confirm'); }}
          onConfirmChange={(v) => { setConfirmPassword(v); fe.clear('confirm'); }}
          passwordLabel={t.register.passwordLabel}
          disabled={submitting}
          wrap={(kind, input) => (
            <Field label={kind === 'password' ? t.register.passwordLabel : t.register.confirmPasswordLabel} {...fe.props(kind === 'password' ? 'password' : 'confirm')}>
              {input}
            </Field>
          )}
        />
        <Button
          appearance="primary"
          onClick={handlePasswordSubmit}
          disabled={submitting || !isValidNewPassword(password, confirmPassword, minLen)}
        >
          {submitting ? <Spinner size="tiny" /> : t.register.submit}
        </Button>
      </>
    )}
  </>);
}
